/**
 * extra.json lifting: IrExtraJson -> effects, videos and their variable tracks (W1-04 core-ir;
 * formats-and-packaging.md P§5.1-P§5.3, DESIGN.md §6.5).
 *
 *  - beats: extra.json carries its own BPM list. When it equals the chart's (what RPE and we write), triples are
 *    used directly (raw non-normalised ones kept in `Effect.src`); otherwise every time is converted through
 *    seconds into chart beats and the import report says so;
 *  - effects keep their order (`effectOrder`); the shader is a built-in name, or "/path" -> the asset of "path"
 *    (a name that is neither is read as a package path and reported); `global`; passthrough;
 *  - variables keep their order: null -> empty anim, a number / [x, y] / [r, g, b, a] -> constant float / vec2 /
 *    colour, keyframes -> an owned track lifted with extra.json keyframe rules (no bezier, slices as written; a
 *    default slice written with both keys is kept in src.sliceRaw so the keys come back; a slice written with one
 *    key is reported as normalised: it comes back with both keys, or none when it is the default);
 *  - videos: path -> asset, start beat, scale mode, alpha / dim (absent or null -> Phira's default, a constant, or
 *    keyframes -> an owned track), attach.line -> the line (an index that does not exist is reported and dropped).
 *    The Video record has no provenance for an explicit `null` alpha / dim or a non-normalised `time` triple
 *    (change request CR-W1-04-6): both are reported as normalised (Phira plays them identically), and are written
 *    back as an absent key and the normalised triple.
 */

import type {
  Effect,
  EffectId,
  EffectShader,
  EffectVar,
  EffectVarType,
  LineId,
  TrackValue,
  TrackValueType,
  Video,
  VideoId,
  VideoVarValue,
} from '../model/types';
import { BUILTIN_SHADER_NAMES } from '../model/schema';
import { createTempoMap } from '../time/tempo';
import { Beats } from '../time/beat';
import type { Beat, TempoMap, Triple } from '../time/types';
import type { Passthrough, RGBA8, Vec2 } from '../util/types';
import { addTrack, type LiftEnv } from './liftLine';
import { extraItems, liftItems } from './liftTrack';
import { tripleIn } from './triples';
import type { IrBpm, IrEffect, IrExtraJson, IrExtraKeyframe, IrVarValue, IrVideo } from './types';

/** Conversion of extra.json beats to chart beats. */
interface ExtraTime {
  /** Chart beat (and the raw triple to keep when the beats are used directly and the triple was not normalised). */
  at(t: Triple): { readonly beat: Beat; readonly raw: Triple | undefined } | null;
  /** The keyframe triple to lift (identity when the BPM lists agree). */
  triple(t: Triple): Triple | null;
}

function sameBpm(extra: IrExtraJson['bpm'], chart: readonly IrBpm[]): boolean {
  if (extra.length !== chart.length) return false;
  return extra.every((p, i) => {
    const c = chart[i];
    if (c === undefined || p.bpm !== c.bpm) return false;
    const a = Beats.tryFromTriple(p.time);
    const b = Beats.tryFromTriple(c.startTime);
    return a !== null && b !== null && Beats.eq(a.beat, b.beat);
  });
}

function extraTime(
  extra: IrExtraJson,
  chartBpm: readonly IrBpm[],
): { readonly time: ExtraTime; readonly retimed: boolean } {
  if (sameBpm(extra.bpm, chartBpm)) {
    return {
      time: { at: (t) => tripleIn(t), triple: (t) => t },
      retimed: false,
    };
  }
  const from: TempoMap = createTempoMap(extra.bpm.map((p) => ({ bpm: p.bpm, startTime: p.time })));
  const to: TempoMap = createTempoMap(chartBpm.map((p) => ({ bpm: p.bpm, startTime: p.startTime })));
  const convert = (t: Triple): Beat | null => {
    if (Beats.tryFromTriple(t) === null) return null;
    const sec = from.secAtTriple(t);
    return Number.isFinite(sec) ? to.beatAt(sec) : null;
  };
  return {
    time: {
      at: (t) => {
        const b = convert(t);
        return b === null ? null : { beat: b, raw: undefined };
      },
      triple: (t) => {
        const b = convert(t);
        return b === null ? null : Beats.toTriple(b);
      },
    },
    retimed: true,
  };
}

const SHADERS: ReadonlySet<string> = new Set(BUILTIN_SHADER_NAMES);

type AnyKeyframe = IrExtraKeyframe<number | readonly number[]>;
type KeyframeVar = Exclude<IrVarValue, null | number | Vec2 | RGBA8>;

function isKeyframeList(v: IrVarValue): v is KeyframeVar {
  if (!Array.isArray(v) || v.length === 0) return false;
  const first: unknown = v[0];
  return typeof first === 'object' && first !== null && !Array.isArray(first);
}

function isNumberArray(v: unknown, len: number): v is readonly number[] {
  return Array.isArray(v) && v.length === len && v.every((x) => typeof x === 'number' && Number.isFinite(x));
}

function isU8(x: number): boolean {
  return Number.isInteger(x) && x >= 0 && x <= 255;
}

function varTypeOf(v: unknown): EffectVarType | null {
  if (typeof v === 'number') return Number.isFinite(v) ? 'float' : null;
  if (isNumberArray(v, 2)) return 'vec2';
  if (isNumberArray(v, 4) && v.every(isU8)) return 'color';
  return null;
}

const VALUE_TYPE: Readonly<Record<EffectVarType, TrackValueType>> = { float: 'number', vec2: 'vec2', color: 'rgba8' };

/** Keyframes with their times converted into chart beats (null when a time cannot be read). */
function retimeKeyframes(kfs: readonly AnyKeyframe[], time: ExtraTime): AnyKeyframe[] | null {
  const out: AnyKeyframe[] = [];
  for (const k of kfs) {
    const s = time.triple(k.startTime);
    const e = time.triple(k.endTime);
    if (s === null || e === null) return null;
    out.push({ ...k, startTime: s, endTime: e });
  }
  return out;
}

function liftVarTrack(
  env: LiftEnv,
  owner:
    | { readonly kind: 'effectVar'; readonly effect: EffectId; readonly name: string }
    | {
        readonly kind: 'videoVar';
        readonly video: VideoId;
        readonly which: 'alpha' | 'dim';
      },
  valueType: TrackValueType,
  kfs: readonly AnyKeyframe[],
  time: ExtraTime,
  path: string,
): ReturnType<typeof addTrack> | null {
  kfs.forEach((k, i) => {
    if ((k.easingLeft === undefined) !== (k.easingRight === undefined))
      env.report('normalized', 'ir.lift.extraSliceOneKey', undefined, `${path}[${String(i)}]`);
  });
  const retimed = retimeKeyframes(kfs, time);
  if (retimed === null) {
    env.report('changed', 'ir.lift.eventSkipped', undefined, path);
    return null;
  }
  const r = liftItems(extraItems(retimed as readonly IrExtraKeyframe<TrackValue>[]));
  for (const i of r.skipped) env.report('changed', 'ir.lift.eventSkipped', undefined, `${path}[${String(i)}]`);
  if (r.keys.length === 0) return null;
  return addTrack(env, owner, valueType, r.keys, false);
}

function liftEffect(env: LiftEnv, ir: IrEffect, ei: number, time: ExtraTime): Effect | null {
  const path = `effects[${String(ei)}]`;
  const start = time.at(ir.start);
  const end = time.at(ir.end);
  if (start === null || end === null) {
    env.report('changed', 'ir.lift.eventSkipped', undefined, path);
    return null;
  }
  const id = env.nextId() as EffectId;
  let shader: EffectShader;
  if (SHADERS.has(ir.shader)) shader = { kind: 'builtin', name: ir.shader as (typeof BUILTIN_SHADER_NAMES)[number] };
  else if (ir.shader.startsWith('/')) shader = { kind: 'asset', asset: env.assetIdForPath(ir.shader.slice(1)) };
  else {
    env.report('changed', 'ir.lift.unknownShader', { shader: ir.shader }, `${path}.shader`);
    shader = { kind: 'asset', asset: env.assetIdForPath(ir.shader) };
  }
  const vars: EffectVar[] = [];
  for (const [name, value] of ir.vars) {
    const vpath = `${path}.vars.${name}`;
    const at = vars.findIndex((v) => v.name === name);
    if (at >= 0) {
      env.report('normalized', 'ir.lift.effectVarDuplicate', { name }, vpath);
      vars.splice(at, 1);
    }
    vars.push(liftEffectVar(env, id, name, value, time, vpath));
  }
  const src: { rawStart?: Triple; rawEnd?: Triple; extra?: Passthrough } = {};
  if (start.raw !== undefined) src.rawStart = start.raw;
  if (end.raw !== undefined) src.rawEnd = end.raw;
  if (ir.extra.length > 0) src.extra = ir.extra;
  return {
    id,
    start: start.beat,
    end: end.beat,
    shader,
    global: ir.global,
    vars,
    ...(Object.keys(src).length > 0 ? { src } : {}),
  };
}

function liftEffectVar(
  env: LiftEnv,
  effect: EffectId,
  name: string,
  value: IrVarValue,
  time: ExtraTime,
  path: string,
): EffectVar {
  if (value === null) return { name, type: 'float', value: { kind: 'null' } };
  if (isKeyframeList(value)) {
    const type = varTypeOf(value[0].start);
    if (type !== null) {
      const track = liftVarTrack(env, { kind: 'effectVar', effect, name }, VALUE_TYPE[type], value, time, path);
      if (track !== null) return { name, type, value: { kind: 'track', track } };
    }
  } else {
    const type = varTypeOf(value);
    if (type !== null) {
      const v = (typeof value === 'number' ? value : [...(value as readonly number[])]) as number | Vec2 | RGBA8;
      return { name, type, value: { kind: 'const', v } };
    }
  }
  env.report('changed', 'ir.lift.effectVarInvalid', { name }, path);
  return { name, type: 'float', value: { kind: 'null' } };
}

function liftVideoVar(
  env: LiftEnv,
  video: VideoId,
  which: 'alpha' | 'dim',
  value: IrVideo['alpha'],
  time: ExtraTime,
  path: string,
): VideoVarValue {
  if (value === undefined) return { kind: 'default' };
  if (value === null) {
    env.report('normalized', 'ir.lift.videoVarNull', { which }, path);
    return { kind: 'default' };
  }
  if (typeof value === 'number') return { kind: 'const', v: value };
  const track = liftVarTrack(env, { kind: 'videoVar', video, which }, 'number', value, time, path);
  return track === null ? { kind: 'default' } : { kind: 'track', track };
}

function liftVideo(env: LiftEnv, ir: IrVideo, vi: number, time: ExtraTime, lineIds: readonly LineId[]): Video | null {
  const path = `videos[${String(vi)}]`;
  const at = time.at(ir.time);
  if (at === null) {
    env.report('changed', 'ir.lift.eventSkipped', undefined, `${path}.time`);
    return null;
  }
  if (at.raw !== undefined) env.report('normalized', 'ir.lift.videoTimeNormalized', undefined, `${path}.time`);
  const id = env.nextId() as VideoId;
  let attachLine: LineId | null = null;
  if (ir.attach !== undefined) {
    const line = lineIds[ir.attach.line];
    if (line !== undefined) attachLine = line;
    else env.report('changed', 'ir.lift.videoAttachMissing', { line: ir.attach.line }, `${path}.attach`);
  }
  return {
    id,
    asset: env.assetIdForPath(ir.path),
    time: at.beat,
    scale: ir.scale,
    alpha: liftVideoVar(env, id, 'alpha', ir.alpha, time, `${path}.alpha`),
    dim: liftVideoVar(env, id, 'dim', ir.dim, time, `${path}.dim`),
    attachLine,
    ...(ir.extra.length > 0 ? { src: { extra: ir.extra } } : {}),
  };
}

export interface ExtraOut {
  readonly effects: Effect[];
  readonly videos: Video[];
}

/** Lifts extra.json (effects in order, then videos). */
export function liftExtraJson(
  env: LiftEnv,
  extra: IrExtraJson,
  chartBpm: readonly IrBpm[],
  lineIds: readonly LineId[],
): ExtraOut {
  const { time, retimed } = extraTime(extra, chartBpm);
  if (retimed && (extra.effects.length > 0 || extra.videos.length > 0)) {
    env.report('normalized', 'ir.lift.extraBpmRetimed', undefined, 'bpm');
  }
  const effects: Effect[] = [];
  extra.effects.forEach((e, i) => {
    const out = liftEffect(env, e, i, time);
    if (out !== null) effects.push(out);
  });
  const videos: Video[] = [];
  extra.videos.forEach((v, i) => {
    const out = liftVideo(env, v, i, time, lineIds);
    if (out !== null) videos.push(out);
  });
  return { effects, videos };
}
